import { motion } from 'framer-motion'
import { LCARSPanel, LCARSBar, Panel } from '@enterpress/holomatrix'
import { 
  Server, 
  Shield, 
  Scaling, 
  Globe,
  Lock,
  Bot,
  Brain,
  Wrench,
  Eye,
  Plug,
  Ship,
  ArrowRight
} from 'lucide-react'
import { useTranslation } from '../i18n/index'

/**
 * Infrastructure Section - Dual Enterprise Foundation
 * 
 * Showcases the two pillars of EnterPress infrastructure:
 * 1. Enterprise WordPress Hosting
 * 2. Enterprise Agent Hosting
 */
export function Infrastructure() {
  const { t } = useTranslation()
  const wordpressFeatures = [
    { icon: Server, label: t('infrastructure.wpFeature1'), description: t('infrastructure.wpFeature1Desc') },
    { icon: Lock, label: t('infrastructure.wpFeature2'), description: t('infrastructure.wpFeature2Desc') },
    { icon: Scaling, label: t('infrastructure.wpFeature3'), description: t('infrastructure.wpFeature3Desc') },
    { icon: Globe, label: t('infrastructure.wpFeature4'), description: t('infrastructure.wpFeature4Desc') },
    { icon: Shield, label: t('infrastructure.wpFeature5'), description: t('infrastructure.wpFeature5Desc') },
  ]

  const agentFeatures = [
    { icon: Bot, label: t('infrastructure.agentFeature1'), description: t('infrastructure.agentFeature1Desc') },
    { icon: Brain, label: t('infrastructure.agentFeature2'), description: t('infrastructure.agentFeature2Desc') },
    { icon: Wrench, label: t('infrastructure.agentFeature3'), description: t('infrastructure.agentFeature3Desc') },
    { icon: Plug, label: t('infrastructure.agentFeature4'), description: t('infrastructure.agentFeature4Desc') },
    { icon: Eye, label: t('infrastructure.agentFeature5'), description: t('infrastructure.agentFeature5Desc') },
  ]

  return (
    <section id="infrastructure" className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(157,140,255,0.02)] to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="purple" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#9D8CFF]">
              {t('infrastructure.title')}
            </h2>
            <LCARSBar color="purple" size="md" className="w-20" />
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('infrastructure.subtitle')}
          </h3>
          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('infrastructure.description')}
          </p>
        </motion.div>

        {/* Two-column infrastructure showcase */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* WordPress Infrastructure */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LCARSPanel title={t('infrastructure.wpTitle')} color="cyan" className="h-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[rgba(0,212,255,0.15)] border border-[rgba(0,212,255,0.3)] flex items-center justify-center">
                    <Server size={24} className="text-[#00D4FF]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">{t('infrastructure.wpSubtitle')}</h4>
                    <p className="text-sm text-[rgba(232,244,248,0.5)]">{t('infrastructure.wpDesc')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {wordpressFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4 p-3 rounded-lg bg-[rgba(0,212,255,0.05)] border border-[rgba(0,212,255,0.15)]"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[rgba(0,212,255,0.1)] flex items-center justify-center flex-shrink-0">
                        <feature.icon size={18} className="text-[#00D4FF]" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{feature.label}</div>
                        <div className="text-xs text-[rgba(232,244,248,0.5)]">{feature.description}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </LCARSPanel>
          </motion.div>

          {/* Agent Infrastructure */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LCARSPanel title={t('infrastructure.agentTitle')} color="purple" className="h-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[rgba(157,140,255,0.15)] border border-[rgba(157,140,255,0.3)] flex items-center justify-center">
                    <Bot size={24} className="text-[#9D8CFF]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">{t('infrastructure.agentSubtitle')}</h4>
                    <p className="text-sm text-[rgba(232,244,248,0.5)]">{t('infrastructure.agentDesc')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {agentFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.label}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4 p-3 rounded-lg bg-[rgba(157,140,255,0.05)] border border-[rgba(157,140,255,0.15)]"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[rgba(157,140,255,0.1)] flex items-center justify-center flex-shrink-0">
                        <feature.icon size={18} className="text-[#9D8CFF]" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{feature.label}</div>
                        <div className="text-xs text-[rgba(232,244,248,0.5)]">{feature.description}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </LCARSPanel>
          </motion.div>
        </div>

        {/* Transporter Room - Migration Feature Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <Panel className="p-0 overflow-hidden">
            <div className="relative">
              {/* Animated beam effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.08)] to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />
              
              <div className="relative p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  {/* Icon */}
                  <motion.div
                    className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[rgba(0,212,255,0.2)] to-[rgba(157,140,255,0.2)] border border-[rgba(0,212,255,0.4)] flex items-center justify-center flex-shrink-0"
                    animate={{
                      boxShadow: [
                        '0 0 20px rgba(0,212,255,0.2)',
                        '0 0 40px rgba(0,212,255,0.4)',
                        '0 0 20px rgba(0,212,255,0.2)',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Ship size={32} className="text-[#00D4FF]" />
                  </motion.div>

                  {/* Content */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                      <h4 className="text-xl md:text-2xl font-bold text-white">
                        {t('infrastructure.transporterTitle')}
                      </h4>
                      <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-[rgba(0,212,255,0.15)] text-[#00D4FF] rounded-full border border-[rgba(0,212,255,0.3)]">
                        {t('common.comingSoon')}
                      </span>
                    </div>
                    <p className="text-[rgba(232,244,248,0.7)] mb-4 max-w-xl">
                      <span className="text-white font-semibold">{t('infrastructure.transporterDescHighlight')}</span>{' '}
                      {t('infrastructure.transporterDescRest')}
                    </p>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm">
                      <div className="flex items-center gap-2 text-[rgba(232,244,248,0.5)]">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00D4FF]" />
                        <span>{t('infrastructure.featureContent')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[rgba(232,244,248,0.5)]">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#9D8CFF]" />
                        <span>{t('infrastructure.featureThemes')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[rgba(232,244,248,0.5)]">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FFB347]" />
                        <span>{t('infrastructure.featureDatabase')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Visual arrow */}
                  <div className="hidden lg:flex items-center gap-3 text-[rgba(232,244,248,0.3)]">
                    <span className="text-sm">Your WP</span>
                    <ArrowRight size={20} />
                    <span className="text-sm text-[#00D4FF]">EnterPress</span>
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </motion.div>

        {/* Unified message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 rounded-xl bg-[rgba(255,179,71,0.08)] border border-[rgba(255,179,71,0.25)]">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-[rgba(0,212,255,0.2)] border-2 border-[#050508] flex items-center justify-center">
                <Server size={14} className="text-[#00D4FF]" />
              </div>
              <div className="w-8 h-8 rounded-full bg-[rgba(157,140,255,0.2)] border-2 border-[#050508] flex items-center justify-center">
                <Bot size={14} className="text-[#9D8CFF]" />
              </div>
            </div>
            <span className="text-sm text-[rgba(232,244,248,0.7)]">
              <span className="text-[#FFB347] font-semibold">{t('infrastructure.unifiedMessageHighlight')}</span>{' '}
              {t('infrastructure.unifiedMessageRest')}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

